import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { IconInvoice } from '../icons';
import { useAuth } from '../context/AuthContext';
import { db, storage, functions } from '../firebaseConfig';
import { collection, query, where, getDocs, orderBy, Timestamp, doc, getDoc, limit } from 'firebase/firestore';
import { ref, getDownloadURL } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { Spinner } from './common/Spinner';
import { useLocation, useNavigate } from 'react-router-dom';
import { useIsReadOnly } from '../hooks/useIsReadOnly';
import { downloadTenantDocumentFile, triggerTenantFileDownload } from '../api/tenantFileApi';
import MessageContextButton from './messaging/MessageContextButton';
import Button from './common/Button';
import { DataTable, Column } from './common/DataTable';
import { RowActionsMenu, RowActionsMenuItem } from './common/RowActionsMenu';
import EmailStatusChip from './EmailStatusChip';
import CancellationStatusChip from './CancellationStatusChip';
import { formatDate } from '../utils/formatters';
import { INVOICE_FORM_ROUTE } from '../config/cockpitAccess';

// Define the structure of an invoice document from Firestore
interface Invoice {
    id: string;
    userId: string;
    invoiceNumber: string;
    invoiceDate: string | Timestamp;
    buyerName: string;
    buyerEmail?: string;
    totalAmount: number;
    currency: string;
    createdAt: Timestamp;
    type: 'zugferd' | 'xrechnung';
    pdfPath?: string;
    xmlPath?: string;
    leitwegId?: string;
    isCancelled?: boolean;
    isCancellation?: boolean;
    cancelledByInvoiceId?: string;
    cancellationForInvoiceId?: string;
    cancellationInvoiceNumber?: string | null;
    emailStatus?: {
        // M10a: 'failed' and 'queued' added to reflect deliveries lifecycle
        status: 'sent' | 'delivered' | 'opened' | 'bounced' | 'failed' | 'queued';
        sentAt?: any;
        deliveredAt?: any;
        openedAt?: any;
        recipientEmail?: string;
        bounceReason?: string;
    };
}

// Kleine, reine Icon-Bausteine für die Aktions-Spalte (RowActionsMenuItem erwartet `icon`, M-V4-B).
const IconPdf = (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"/></svg>
);
const IconXml = (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/></svg>
);
const IconStorno = (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"/></svg>
);
const IconEmail = (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg>
);

/** Sortier-Hilfswert für Firestore-Datumsfelder (Timestamp/String) → Millisekunden. */
const toSortMillis = (value: unknown): number => {
    if (!value) return 0;
    if (value instanceof Timestamp) return value.toMillis();
    if (typeof (value as { seconds?: number })?.seconds === 'number') {
        return (value as { seconds: number }).seconds * 1000;
    }
    const d = new Date(value as string | number);
    return Number.isNaN(d.getTime()) ? 0 : d.getTime();
};

const InvoiceArchive: React.FC = () => {
    const { t, i18n } = useTranslation(['invoice', 'common']);
    const { currentUser, effectiveScopeUid } = useAuth();
    // M11b-3: advisor (read-only) sieht keine mutierenden Aktionen (Storno/Resend).
    // Der Download/Öffnen-Button (Lesevorgang) bleibt bewusst erhalten.
    const isReadOnly = useIsReadOnly();
    const [invoices, setInvoices] = useState<Invoice[]>([]);
    const [filteredInvoices, setFilteredInvoices] = useState<Invoice[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [downloading, setDownloading] = useState<string | null>(null); // Track which invoice is downloading

    // Email-Versand (erneut)
    const [sendEmailFor, setSendEmailFor] = useState<string | null>(null); // invoice.id with open email form
    const [emailInput, setEmailInput] = useState<string>('');
    const [emailSendStatus, setEmailSendStatus] = useState<Record<string, 'sending' | 'success' | 'error'>>({});
    // M10a A2: B2G warning state — invoice.id awaiting user confirmation of email send to B2G address
    const [b2gWarningFor, setB2gWarningFor] = useState<string | null>(null);

    // Suchparameter
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [isFiltered, setIsFiltered] = useState<boolean>(false);
    const [typeFilter, setTypeFilter] = useState<'all' | 'zugferd' | 'xrechnung'>('all');

    // ── Generationen-Zaehler gegen veraltete Ladeantworten ────────────────────
    //
    // `loadGenerationRef` zaehlt bei jedem Lade-Durchlauf hoch (Muster wie
    // InboxPage.tsx): eine ueberholte Antwort darf weder die Liste noch
    // `loading` schreiben. Das ist hier kein Randfall — der Lade-Effekt feuert
    // mehrfach pro Seitenaufruf, siehe die `tRef`-Begruendung unten.
    //
    // `mutationGenerationRef` deckt ab, was eine reine Anfrage-Generation nicht
    // abdeckt: das optimistische `emailStatus`-Update in `handleSendEmail`
    // aendert die Liste nur lokal. Eine Anfrage, die VOR diesem Update startete
    // und DANACH zurueckkommt, ist die juengste Anfrage, kennt den neuen Status
    // aber nicht — der Versand-Haken wuerde wieder verschwinden.
    //
    // Kunden- und Produktliste haben diesen zweiten Zaehler inzwischen durch
    // ein Mutations-Log ersetzt (Serverantwort = Basis, lokales Delta darueber
    // gelegt). Hier bleibt der Zaehler bewusst stehen: die Begruendung steht in
    // `architecture-list-concurrency-merge.md` §6.3. Kurzfassung — es gibt in
    // diesem Archiv weder einen Anlege- noch einen Loeschpfad, die einzige
    // Mutation setzt eine bereits gerenderte Zeile voraus. Eine verworfene
    // Antwort kann die Liste hier also nie leeren, sondern nur ihre
    // Aktualisierung um eine Ladung verzoegern. Der Umbau waere zugleich der
    // teuerste der vier Listen (zwei Listen-States, URL-`?id=`-Zweig,
    // Filter-Zustand) — groesste Flaeche, kleinster Nutzen.
    const loadGenerationRef = useRef(0);
    const mutationGenerationRef = useRef(0);

    // `t` wird im Lade-Effekt nur fuer Fehlermeldungen gebraucht und ist bewusst
    // KEINE Dependency: `src/i18n/config.ts` bindet react-i18next mit
    // `bindI18n: 'languageChanged loaded'` und `bindI18nStore: 'added removed'`
    // an 27 einzeln ueber HTTP nachgeladene Namespaces. Jedes eintreffende
    // Bundle liefert eine neue `t`-Referenz und haette so einen weiteren
    // vollstaendigen Firestore-Read plus Lade-Spinner ausgeloest. Ueber das Ref
    // bleibt die Fehlermeldung trotzdem in der aktuell aktiven Sprache.
    const tRef = useRef(t);
    tRef.current = t;

    // URL-Parameter auslesen
    const location = useLocation();
    const navigate = useNavigate();
    const queryParams = new URLSearchParams(location.search);
    const invoiceIdFromUrl = queryParams.get('id');

    useEffect(() => {
        const myGen = ++loadGenerationRef.current;
        const myMutationGen = mutationGenerationRef.current;
        /** Eine neuere Anfrage laeuft — deren Antwort gewinnt. */
        const isSuperseded = () => myGen !== loadGenerationRef.current;
        /** Seit dem Anfragestart wurde lokal mutiert — dieser Snapshot ist veraltet. */
        const isPreMutation = () => myMutationGen !== mutationGenerationRef.current;

        const fetchInvoices = async () => {
            if (!currentUser) {
                setError(tRef.current('archive.errors.userNotLoggedIn'));
                setLoading(false);
                return;
            }

            setLoading(true);
            setError(null);

            try {
                console.log(`Fetching invoices for user: ${effectiveScopeUid}`);

                if (invoiceIdFromUrl) {
                    console.log(`Spezifische Rechnungs-ID aus URL: ${invoiceIdFromUrl}`);
                    setSearchTerm(invoiceIdFromUrl); // Suchbegriff im UI setzen (optional, kann auch Rechnungsnummer sein)

                    let foundInvoice: Invoice | null = null;

                    // Da wir jetzt immer die Firestore-ID erwarten, machen wir einen direkten Lookup
                    console.log(`Versuche, Rechnung über Firestore Document ID zu finden: ${invoiceIdFromUrl}`);
                    const invoiceDocRef = doc(db, 'invoices', invoiceIdFromUrl);
                    const invoiceDoc = await getDoc(invoiceDocRef);

                    if (invoiceDoc.exists() && invoiceDoc.data().userId === effectiveScopeUid) {
                        const data = invoiceDoc.data() as Omit<Invoice, 'id'>;
                        const invoiceType = data.type || (data.pdfPath ? 'zugferd' : 'xrechnung');
                        foundInvoice = {
                            id: invoiceDoc.id,
                            ...data,
                            type: invoiceType as 'zugferd' | 'xrechnung'
                        };
                        console.log(`Rechnung über Document ID gefunden:`, foundInvoice.id);
                    }

                    const allUserInvoices = await fetchAllUserInvoices(); // Immer alle laden für Reset-Funktion
                    if (isSuperseded() || isPreMutation()) return;
                    setInvoices(allUserInvoices);

                    if (foundInvoice) {
                        setFilteredInvoices([foundInvoice]);
                        setIsFiltered(true);
                        console.log(`Filter auf gefundene Rechnung gesetzt: ${foundInvoice.id}`);
                    } else {
                        console.log('Spezifische Rechnung nicht über Firestore ID gefunden, lade alle unfiltert.');
                        setFilteredInvoices(allUserInvoices);
                        setIsFiltered(false);
                        // Optional: Suchbegriff zurücksetzen, wenn nichts gefunden wurde und es eine ID war.
                        // Wenn es eine Rechnungsnummer war, könnte man den Suchbegriff beibehalten.
                        // Fürs Erste lassen wir den Suchbegriff gesetzt.
                    }

                } else {
                    await loadAndSetAllInvoices(); // Keine ID in URL, alle Rechnungen normal laden
                }
            } catch (err) {
                if (isSuperseded()) return;
                console.error("Error fetching invoices: ", err);
                setError(tRef.current('archive.errors.loadingError'));
                if (err instanceof Error) {
                     console.error(err.message);
                     console.error(err.stack);
                }
            } finally {
                // Nur die juengste Anfrage besitzt den Lade-Zustand. Eine bloss
                // durch eine lokale Mutation entwertete Antwort ist die
                // juengste und muss `loading` aufloesen.
                if (!isSuperseded()) setLoading(false);
            }
        };

        // Hilfsfunktion, um alle Rechnungen zu laden und zu setzen
        const loadAndSetAllInvoices = async () => {
            const allUserInvoices = await fetchAllUserInvoices();
            if (isSuperseded() || isPreMutation()) return;
            setInvoices(allUserInvoices);
            setFilteredInvoices(allUserInvoices); // Initial keine Filterung
            setIsFiltered(false); // Kein Filter aktiv
            console.log('Alle Rechnungen geladen:', allUserInvoices.length);
        };

        // Hilfsfunktion, um ALLE Rechnungen des Benutzers abzurufen
        const fetchAllUserInvoices = async (): Promise<Invoice[]> => {
            if (!currentUser) return [];
            const invoicesRef = collection(db, 'invoices');
            const invoicesQuery = query(
                invoicesRef,
                where('userId', '==', effectiveScopeUid),
                orderBy('createdAt', 'desc')
            );
            const invoicesSnapshot = await getDocs(invoicesQuery);
            const userInvoices: Invoice[] = [];
            invoicesSnapshot.forEach((doc) => {
                const data = doc.data() as Omit<Invoice, 'id'>;
                const invoiceType = data.type || (data.pdfPath ? 'zugferd' : 'xrechnung');
                const rawData = (doc.data() as any).rawData;
                const buyerEmail = rawData?.buyer?.email || undefined;
                const emailStatus = (doc.data() as any).emailStatus || undefined;
                userInvoices.push({
                    id: doc.id,
                    ...data,
                    buyerEmail,
                    emailStatus,
                    type: invoiceType as 'zugferd' | 'xrechnung'
                });
            });
            return userInvoices;
        };

        fetchInvoices(); // Haupt-Ladefunktion aufrufen
    }, [currentUser, effectiveScopeUid, invoiceIdFromUrl]); // invoiceIdFromUrl als Abhängigkeit hinzufügen

    // Filter anwenden, wenn sich invoices oder Filter ändern
    useEffect(() => {
        if (invoices.length > 0) {
            applyFilters(searchTerm, typeFilter);
        }
    }, [invoices]); // eslint-disable-line react-hooks/exhaustive-deps

    // Suchfunktion
    const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        const term = e.target.value.toLowerCase();
        setSearchTerm(term);
        applyFilters(term, typeFilter);
    };

    // Typ-Filter-Funktion
    const handleTypeFilter = (type: 'all' | 'zugferd' | 'xrechnung') => {
        setTypeFilter(type);
        applyFilters(searchTerm, type);
    };

    // Kombinierte Filter-Funktion
    const applyFilters = (searchTerm: string, typeFilter: 'all' | 'zugferd' | 'xrechnung') => {
        if (searchTerm.trim() === '' && typeFilter === 'all') {
            setFilteredInvoices(invoices);
            setIsFiltered(false);

            // URL-Parameter entfernen, wenn alle Filter geleert werden
            if (invoiceIdFromUrl) {
                navigate('/dashboard/archiv');
            }
            return;
        }

        let filtered = invoices;

        // Nach Typ filtern
        if (typeFilter !== 'all') {
            filtered = filtered.filter(invoice => invoice.type === typeFilter);
        }

        // Nach Suchbegriff filtern
        if (searchTerm.trim() !== '') {
            filtered = filtered.filter(invoice =>
                (invoice.invoiceNumber && invoice.invoiceNumber.toLowerCase().includes(searchTerm)) ||
                (invoice.buyerName && invoice.buyerName.toLowerCase().includes(searchTerm)) ||
                (invoice.id && invoice.id.toLowerCase().includes(searchTerm))
            );
        }

        setFilteredInvoices(filtered);
        setIsFiltered(searchTerm.trim() !== '' || typeFilter !== 'all');
    };

    // Filter zurücksetzen
    const handleResetFilter = () => {
        setSearchTerm('');
        setTypeFilter('all');
        setFilteredInvoices(invoices);
        setIsFiltered(false);

        // URL-Parameter entfernen
        if (invoiceIdFromUrl) {
            navigate('/dashboard/archiv');
        }
    };

    /**
     * ⭐ NEU: Handler für Stornorechnung-Erstellung
     */
    const handleCreateCancellation = (invoice: Invoice) => {
        if (!invoice.invoiceNumber) {
            alert(t('cancellation.errors.missingInvoiceNumber', 'Rechnungsnummer fehlt'));
            return;
        }

        // Root Cause (Support-Ticket Irene Bayer, 2026-08-01): dieser Call navigierte
        // bisher auf den bloßen `/dashboard`-Index. Seit der GA-Freischaltung des
        // Owner-Cockpits (REACT_APP_UX_COCKPIT_ENABLED=true, 2026-07-26) rendert der
        // Index für ALLE Nutzer `CockpitPage` statt `InvoiceDashboard` — die
        // `cancellationFor`/`invoiceNumber`-Query-Params landeten in der URL, wurden
        // aber nie ausgelesen (CockpitPage verarbeitet sie nicht). Für die Nutzerin sah
        // das wie "Klick tut nichts" aus. Fix: IMMER `INVOICE_FORM_ROUTE` verwenden
        // (die echte, flag-freie, garantiert auf `InvoiceDashboard` zeigende Konstante aus
        // `architecture-cockpit-route-split.md` §3 E2) statt eines hartkodierten
        // `/dashboard`-Literals.
        navigate(`${INVOICE_FORM_ROUTE}?cancellationFor=${invoice.id}&invoiceNumber=${encodeURIComponent(invoice.invoiceNumber)}`);
    };

    const handleSendEmail = async (invoice: Invoice) => {
        if (!emailInput.trim()) return;
        setEmailSendStatus(prev => ({ ...prev, [invoice.id]: 'sending' }));
        try {
            const resend = httpsCallable(functions, 'resendDocumentEmail');
            await resend({ docType: 'invoice', docId: invoice.id, recipientEmail: emailInput.trim() });
            setEmailSendStatus(prev => ({ ...prev, [invoice.id]: 'success' }));
            // Optimistisches Update: emailStatus sofort im lokalen State setzen
            const sentEmail = emailInput.trim();
            mutationGenerationRef.current += 1;
            setInvoices(prev => prev.map(inv => inv.id === invoice.id
                ? { ...inv, emailStatus: { status: 'sent', recipientEmail: sentEmail } }
                : inv
            ));
            setFilteredInvoices(prev => prev.map(inv => inv.id === invoice.id
                ? { ...inv, emailStatus: { status: 'sent', recipientEmail: sentEmail } }
                : inv
            ));
            setTimeout(() => {
                setSendEmailFor(null);
                setEmailInput('');
                setEmailSendStatus(prev => { const s = { ...prev }; delete s[invoice.id]; return s; });
            }, 2500);
        } catch {
            setEmailSendStatus(prev => ({ ...prev, [invoice.id]: 'error' }));
        }
    };

    const handleDownload = async (invoice: Invoice) => {
        // Typ-spezifische Validierung
        const filePath = invoice.type === 'zugferd' ? invoice.pdfPath : invoice.xmlPath;

        if (!filePath) {
            const fileType = invoice.type === 'zugferd' ? 'PDF' : 'XML';
            setError(t('archive.errors.pathNotFound', { fileType }));
            return;
        }

        setDownloading(invoice.id);
        setError(null);

        try {
            const fileExtension = invoice.type === 'zugferd' ? 'pdf' : 'xml';
            const filePrefix = invoice.type === 'zugferd' ? t('archive.filePrefix.invoice') || 'Invoice' : t('archive.filePrefix.xrechnung') || 'XRechnung';
            const fileName = `${filePrefix}_${invoice.invoiceNumber || invoice.id}.${fileExtension}`;

            // Advisor-Kontext (cross-tenant read-only): geladene Daten gehören einem fremden
            // Scope → Storage-Direktpfad/PHP-Proxy scheitern. Eine tenant-aware Callable für
            // PDF UND XML. Owner-Zweige unten bleiben unverändert.
            if (effectiveScopeUid !== currentUser?.uid) {
                const fileKind = invoice.type === 'zugferd' ? 'pdf' : 'xml';
                const file = await downloadTenantDocumentFile('invoice', invoice.id, fileKind);
                triggerTenantFileDownload(file, fileName);
                return;
            }

            if (invoice.type === 'xrechnung') {
                // Für XML-Dateien: Backend-Proxy verwenden
                console.log(`Downloading XML via backend proxy for invoice: ${invoice.id}`);

                const token = await currentUser?.getIdToken();
                if (!token) {
                    throw new Error(t('archive.errors.userNotAuthenticated'));
                }

                const response = await fetch(`${process.env.REACT_APP_API_URL}/download-xml/${invoice.id}`, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                });

                console.log('Download response:', {
                    status: response.status,
                    statusText: response.statusText,
                    headers: Object.fromEntries(response.headers.entries()),
                    contentType: response.headers.get('content-type'),
                    contentLength: response.headers.get('content-length')
                });

                if (!response.ok) {
                    const errorText = await response.text();
                    console.error('Download error response:', errorText);
                    throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
                }

                const blob = await response.blob();
                console.log('Downloaded blob:', {
                    size: blob.size,
                    type: blob.type,
                    fileName: fileName
                });

                // Prüfe, ob der Blob Daten enthält
                if (blob.size === 0) {
                    throw new Error(t('archive.errors.emptyFile'));
                }

                const blobUrl = window.URL.createObjectURL(blob);
                console.log('Created blob URL:', blobUrl);

                const link = document.createElement('a');
                link.href = blobUrl;
                link.download = fileName;
                link.style.display = 'none';
                document.body.appendChild(link);

                console.log('Triggering download with link:', {
                    href: link.href,
                    download: link.download,
                    fileName: fileName
                });

                link.click();
                document.body.removeChild(link);

                // Blob-URL nach kurzer Zeit freigeben
                setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
            } else {
                // Für PDF-Dateien: Direkter Firebase Storage Link
                console.log(`Attempting to get download URL for ${invoice.type.toUpperCase()} path: ${filePath}`);
                const fileRef = ref(storage, filePath);
                const url = await getDownloadURL(fileRef);
                console.log(`Successfully got download URL: ${url}`);

                const link = document.createElement('a');
                link.href = url;
                link.target = '_blank';
                link.rel = 'noopener noreferrer';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }
        } catch (err) {
             console.error("Error getting download URL or downloading file: ", err);
             if (err instanceof Error && 'code' in err) {
                 switch ((err as any).code) {
                    case 'storage/object-not-found':
                        setError(`${t('archive.errors.fileNotFound')}`);
                        break;
                    case 'storage/unauthorized':
                    case 'functions/permission-denied':
                        setError(`${t('archive.errors.unauthorized')}`);
                        break;
                    case 'functions/resource-exhausted':
                        setError(`${t('archive.errors.downloadError')}`);
                        break;
                    default:
                        setError(`${t('archive.errors.downloadError')}`);
                 }
             } else {
                setError(`${t('archive.errors.unknownError')}`);
             }
             if (err instanceof Error) {
                 console.error(err.message);
                 console.error(err.stack);
            }
        } finally {
             setDownloading(null);
        }
    };

    /** E-Mail-Versand-Formular öffnen/schließen (unverändertes Verhalten, nur ausgelagert). */
    const toggleEmailForm = (invoice: Invoice) => {
        if (sendEmailFor === invoice.id) {
            setSendEmailFor(null);
            setB2gWarningFor(null);
        } else {
            setSendEmailFor(invoice.id);
            setEmailInput(invoice.buyerEmail || '');
            setEmailSendStatus(prev => { const s = { ...prev }; delete s[invoice.id]; return s; });
            // M10a A2: show B2G warning if leitwegId present (B2G = public sector)
            if (invoice.type === 'xrechnung' && invoice.leitwegId) {
                setB2gWarningFor(invoice.id);
            } else {
                setB2gWarningFor(null);
            }
        }
    };

    // Email — M10a: gate open for XRechnung (xmlPath) + ZUGFeRD (pdfPath)
    // M11b-3: für advisor (read-only) ausgeblendet (Versand = mutierend).
    // Wiederverwendet für EmailStatusChip-Sichtbarkeit UND das Email-Menü-Item (M-V4-B).
    const hasEmailDeliveryPath = (invoice: Invoice): boolean =>
        (invoice.type === 'zugferd' && Boolean(invoice.pdfPath)) ||
        (invoice.type === 'xrechnung' && Boolean(invoice.xmlPath));

    // Menü-Items der Aktions-Spalte (M-V4-B, architecture-ux-v4.md §2.1) — Reihenfolge
    // = Häufigkeit: Download (immer) → E-Mail (Versandpfad + !isReadOnly) → Storno
    // (!isReadOnly && weder storniert noch Stornorechnung). Gating 1:1 aus dem Bestand.
    const buildInvoiceActionItems = (invoice: Invoice): RowActionsMenuItem[] => {
        const items: RowActionsMenuItem[] = [
            {
                id: 'download',
                label: invoice.type === 'zugferd' ? t('archive.table.tooltips.openPdf') : t('archive.table.tooltips.downloadXml'),
                icon: invoice.type === 'zugferd' ? IconPdf : IconXml,
                variant: 'accent',
                loading: downloading === invoice.id,
                onSelect: () => handleDownload(invoice),
            },
        ];
        if (!isReadOnly && hasEmailDeliveryPath(invoice)) {
            items.push({
                id: 'email',
                label: t('common:emailSend.resendButton'),
                icon: IconEmail,
                onSelect: () => toggleEmailForm(invoice),
            });
        }
        // Storno — M11b-3: für advisor (read-only) ausgeblendet.
        if (!isReadOnly && !invoice.isCancelled && !invoice.isCancellation) {
            items.push({
                id: 'storno',
                label: t('cancellation.button.tooltip', 'Stornorechnung erstellen'),
                icon: IconStorno,
                variant: 'danger',
                onSelect: () => handleCreateCancellation(invoice),
            });
        }
        return items;
    };

    // Aktions-Spalte je Zeile (D-V4-1, architecture-ux-v4.md §2.1): Storno-/
    // Stornorechnung-Info-Text und EmailStatusChip bleiben SICHTBAR (Status-
    // Anzeigen, keine Aktionen); MessageContextButton bleibt außerhalb des
    // Menüs (Live-Unread-Indikator wäre in einem geschlossenen Menü unsichtbar);
    // Download/E-Mail/Storno wandern gemeinsam ins RowActionsMenu.
    const renderActions = (invoice: Invoice) => (
        <div className="flex items-center gap-1 justify-end">
            <CancellationStatusChip
                isCancelled={invoice.isCancelled}
                isCancellation={invoice.isCancellation}
                cancellationInvoiceNumber={invoice.cancellationInvoiceNumber}
            />
            {/* emailStatus-Minitext → EmailStatusChip (§5.7/§9); undefined ⇒ null.
                Sichtbarkeits-Gate 1:1 wie zuvor (Parität, Bestand war Kind
                desselben !isReadOnly-Blocks). */}
            {hasEmailDeliveryPath(invoice) && !isReadOnly && (
                <EmailStatusChip emailStatus={invoice.emailStatus} locale={i18n.language} />
            )}
            {/* M-MSG-3: Nachrichten-Thread zur Rechnung — additiv, NICHT
                !isReadOnly-gated (advisor darf lesen & posten), nur hinter
                canUseMessaging(effectiveScopeUid) (Button selbst rendert
                null ohne Zugriff). */}
            <MessageContextButton
                context={{ type: 'invoice', refId: invoice.id }}
                contextSummary={{
                    title: invoice.invoiceNumber || invoice.id,
                    subtitle: `${invoice.buyerName || t('archive.table.values.unknown')} · ${formatDate(invoice.createdAt, { locale: i18n.language })}`,
                    amountLabel: invoice.totalAmount
                        ? `${Number(invoice.totalAmount).toFixed(2)} ${invoice.currency || 'EUR'}`
                        : undefined,
                    badgeLabel: invoice.type === 'zugferd' ? t('archive.filter.zugferd') : t('archive.filter.xrechnung'),
                    badgeVariant: invoice.type,
                    hrefToRef: `/dashboard/archiv?id=${invoice.id}`,
                }}
            />
            <RowActionsMenu items={buildInvoiceActionItems(invoice)} />
        </div>
    );

    // E-Mail-Versand-Sub-Row: NUR für die Zeile mit offenem Formular
    // (sendEmailFor === invoice.id). Inhalt 1:1 aus dem Bestand (B2G-Warnung,
    // Eingabefeld, Senden/Abbrechen, Sende-Status). DataTable liefert das
    // umschließende <tr><td colSpan> selbst.
    const renderRowDetail = (invoice: Invoice) => {
        if (sendEmailFor !== invoice.id) return null;
        return (
            <>
                {/* M10a A2: B2G warning — leitwegId present means likely public sector */}
                {b2gWarningFor === invoice.id && (
                    <div className="mb-3 flex items-start gap-2 rounded bg-[var(--tone-warning-bg)] border border-[var(--tone-warning-border)] px-3 py-2 text-sm text-[var(--tone-warning-fg)]">
                        <svg className="w-4 h-4 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/></svg>
                        <span>
                            {t('common:emailSend.b2gWarning', { leitwegId: invoice.leitwegId, defaultValue: 'Diese Rechnung enthält eine Leitweg-ID ({{leitwegId}}) und ist möglicherweise für einen öffentlichen Auftraggeber bestimmt. B2G-Rechnungen werden in der Regel über PEPPOL/OZG-Portale eingereicht, nicht per E-Mail. Sie können trotzdem per E-Mail versenden.' })}
                        </span>
                    </div>
                )}
                <div className="flex items-center gap-3 flex-wrap">
                    <span className="text-sm text-muted shrink-0">{t('common:emailSend.emailLabel')}:</span>
                    <input
                        type="email"
                        value={emailInput}
                        onChange={e => setEmailInput(e.target.value)}
                        placeholder={t('common:emailSend.resendInputPlaceholder')}
                        className="px-3 py-1.5 text-sm bg-surface-inset border border-strong rounded text-primary focus:outline-none focus:border-indigo-400 w-64"
                        disabled={emailSendStatus[invoice.id] === 'sending'}
                        onKeyDown={e => e.key === 'Enter' && handleSendEmail(invoice)}
                        autoFocus
                    />
                    <button
                        onClick={() => handleSendEmail(invoice)}
                        disabled={!emailInput.trim() || emailSendStatus[invoice.id] === 'sending'}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-indigo-600 hover:bg-indigo-500 text-[#0C1322] rounded disabled:opacity-50 transition-colors"
                    >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"/></svg>
                        {emailSendStatus[invoice.id] === 'sending' ? t('common:emailSend.resendSending') : t('common:emailSend.resendButton')}
                    </button>
                    <button onClick={() => { setSendEmailFor(null); setB2gWarningFor(null); }} className="text-sm text-muted hover:text-secondary transition-colors">
                        {t('cancel', { ns: 'common', defaultValue: 'Abbrechen' })}
                    </button>
                    {emailSendStatus[invoice.id] === 'success' && <span className="text-[var(--tone-success-fg)] text-sm">{t('common:emailSend.resendSuccess')}</span>}
                    {emailSendStatus[invoice.id] === 'error' && <span className="text-[var(--tone-danger-fg)] text-sm">{t('common:emailSend.resendError')}</span>}
                </div>
            </>
        );
    };

    // Spalten-Definition (§5.5): Spalten-Diät (Typ/Datum hideBelow md, Käufer
    // truncate, Aktionen sticky right), density="compact" am DataTable.
    const columns: Column<Invoice>[] = [
        {
            id: 'invoiceNumber',
            header: t('archive.table.headers.invoiceNumber'),
            accessor: (invoice) => (
                <span className="font-medium text-primary">
                    {invoice.invoiceNumber || t('archive.table.values.unknown')}
                </span>
            ),
            sortValue: (invoice) => invoice.invoiceNumber ?? '',
        },
        {
            id: 'type',
            header: t('archive.table.headers.type'),
            hideBelow: 'md',
            accessor: (invoice) => (
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    invoice.type === 'zugferd'
                        ? 'bg-blue-100 text-blue-800'
                        : 'bg-green-100 text-green-800'
                }`}>
                    {invoice.type === 'zugferd' ? t('archive.filter.zugferd') : t('archive.filter.xrechnung')}
                </span>
            ),
            sortValue: (invoice) => invoice.type,
        },
        {
            id: 'date',
            header: t('archive.table.headers.date'),
            hideBelow: 'md',
            accessor: (invoice) => formatDate(invoice.createdAt, { locale: i18n.language }),
            sortValue: (invoice) => toSortMillis(invoice.createdAt),
        },
        {
            id: 'buyer',
            header: t('archive.table.headers.buyer'),
            truncate: 'max-w-[14rem]',
            accessor: (invoice) => invoice.buyerName || t('archive.table.values.unknown'),
            sortValue: (invoice) => invoice.buyerName ?? '',
        },
        {
            id: 'totalAmount',
            header: t('archive.table.headers.totalAmount'),
            numeric: true,
            accessor: (invoice) => invoice.totalAmount
                ? `${Number(invoice.totalAmount).toFixed(2)} ${invoice.currency || 'EUR'}`
                : t('archive.table.values.unknown'),
            sortValue: (invoice) => invoice.totalAmount ?? 0,
        },
        {
            id: 'actions',
            header: t('archive.table.headers.actions'),
            sticky: 'right',
            accessor: (invoice) => renderActions(invoice),
        },
    ];

    return (
        <div>
            <div className="max-w-5xl mx-auto bg-surface-raised p-6 rounded-lg shadow-md">
                <h2 className="text-2xl font-bold text-center text-primary mb-6">{t('archive.title')}</h2>

                {/* Suchfeld */}
                <div className="mb-6">
                    <div className="flex flex-col md:flex-row gap-4">
                        <div className="flex-grow">
                            <input
                                type="text"
                                placeholder={t('archive.searchPlaceholder')}
                                value={searchTerm}
                                onChange={handleSearch}
                                className="w-full px-4 py-2 bg-surface-inset text-primary border border-strong rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            />
                        </div>
                        <div className="flex gap-2">
                            <select
                                value={typeFilter}
                                onChange={(e) => handleTypeFilter(e.target.value as 'all' | 'zugferd' | 'xrechnung')}
                                className="px-4 py-2 bg-surface-inset text-primary border border-strong rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            >
                                <option value="all">{t('archive.filter.allTypes')}</option>
                                <option value="zugferd">{t('archive.filter.zugferd')}</option>
                                <option value="xrechnung">{t('archive.filter.xrechnung')}</option>
                            </select>
                            {isFiltered && (
                                <button
                                    onClick={handleResetFilter}
                                    className="px-4 py-2 bg-surface-inset text-primary border border-strong rounded hover:bg-surface-hover transition-colors"
                                >
                                    {t('archive.resetFilter')}
                                </button>
                            )}
                        </div>
                    </div>
                    {isFiltered && (
                        <p className="mt-2 text-sm text-muted">
                            {t('archive.filteredCount', { count: filteredInvoices.length, total: invoices.length })}
                            {typeFilter !== 'all' && ` (${t('archive.filter.type', { type: typeFilter === 'zugferd' ? 'ZUGFeRD' : 'XRechnung' })})`}
                            {invoiceIdFromUrl ? ` (${t('archive.filteredById', { id: invoiceIdFromUrl })})` : ''}
                        </p>
                    )}
                </div>

                {error && (
                    <div className="mb-4 p-4 bg-[var(--tone-danger-bg)] border border-[var(--tone-danger-border)] text-[var(--tone-danger-fg)] rounded" role="alert">
                        <strong className="font-bold">{t('archive.errors.error')}:</strong>
                        <span className="block sm:inline"> {error}</span>
                    </div>
                )}

                {loading ? (
                    <div className="flex justify-center items-center py-10">
                        <Spinner size="lg" />
                    </div>
                ) : (
                    <>
                        {filteredInvoices.length === 0 ? (
                            <div className="text-center py-10 text-secondary">
                                {isFiltered ? (
                                    <>
                                        <p>{t('archive.emptyState.noResults')}</p>
                                        <button
                                            onClick={handleResetFilter}
                                            className="mt-4 px-4 py-2 bg-indigo-600 text-[#0C1322] rounded hover:bg-indigo-700 transition-colors"
                                        >
                                            {t('archive.emptyState.showAll')}
                                        </button>
                                    </>
                                ) : (
                                    <div className="py-6">
                                        <IconInvoice size={48} className="text-gray-600 mx-auto mb-4" />
                                        <p className="text-secondary font-medium">{t('archive.emptyState.noInvoices')}</p>
                                        <p className="mt-1 text-sm text-muted">{t('archive.emptyState.noInvoicesSubtext')}</p>
                                        {/* M11b-3: advisor (read-only) darf keine Rechnung erstellen — CTA ausgeblendet. */}
                                        {!isReadOnly && (
                                            <Button
                                                onClick={() => navigate(INVOICE_FORM_ROUTE)}
                                                className="mt-5"
                                            >
                                                {t('archive.emptyState.ctaCreateInvoice')}
                                            </Button>
                                        )}
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="mt-6">
                                <DataTable
                                    columns={columns}
                                    rows={filteredInvoices}
                                    rowKey={(invoice) => invoice.id}
                                    density="compact"
                                    empty={<span>{t('archive.emptyState.noResults')}</span>}
                                    renderRowDetail={renderRowDetail}
                                    rowClassName={(invoice) => invoice.id === invoiceIdFromUrl ? 'bg-[var(--tone-accent-bg)]' : undefined}
                                />
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default InvoiceArchive;
